import React, { useState, useEffect } from 'react';
import TickerSearchInput from '../components/TickerSearchInput'; // NEWS-MULTI-SOURCE
import NewsFeed from '../components/NewsFeed'; // NEWS-MULTI-SOURCE: reuse StockPage's component for company mode

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function timeAgo(unix) {
  const diff = Date.now() / 1000 - unix;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getSentiment(headline) {
  if (!headline) return 'NEUTRAL';
  const h = headline.toLowerCase();
  const bullish = ['surge', 'soar', 'beat', 'record', 'growth', 'profit', 'upgrade',
    'outperform', 'rises', 'gains', 'strong', 'exceeds', 'jumps', 'rally', 'positive',
    'boosts', 'tops', 'higher', 'expands', 'wins', 'launches', 'milestone'];
  const bearish = ['fall', 'drop', 'miss', 'loss', 'decline', 'downgrade', 'crash',
    'weak', 'below', 'cut', 'layoff', 'warning', 'risk', 'concern', 'lower', 'slump',
    'tumble', 'plunge', 'disappoints', 'probe', 'lawsuit', 'recall', 'halts'];
  const bScore = bullish.filter(w => h.includes(w)).length;
  const eScore = bearish.filter(w => h.includes(w)).length;
  if (bScore > eScore) return 'BULLISH';
  if (eScore > bScore) return 'BEARISH';
  return 'NEUTRAL';
}

const SENTIMENT_STYLE = {
  BULLISH: { color: '#00ff88', bg: 'rgba(0,255,136,0.08)', border: 'rgba(0,255,136,0.2)' },
  BEARISH: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)'  },
  NEUTRAL: { color: '#64748b', bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.2)' },
};

// Renamed from "All News" — that label implied comprehensive coverage
// (including Fed releases and press releases, which live in their own
// tabs) when it's actually just Finnhub's general wire feed (Reuters/CNBC
// style market headlines). "Market News" says what it actually is.
// isSpecial: true means this tab hits its OWN dedicated backend endpoint
// (not a `category=` variant of the Finnhub general feed) — Fed & Economy
// is official Federal Reserve releases, not third-party reporting; Press
// Releases is the raw company wire feed, not journalism. Order here is
// the deliberate display order, not grouped by data source.
const TABS = [
  { id: 'general',        label: 'Market News',   isSpecial: false },
  { id: 'economy',        label: 'Fed & Economy', isSpecial: true  },
  { id: 'forex',          label: 'Forex',         isSpecial: false },
  { id: 'crypto',         label: 'Crypto',        isSpecial: false },
  { id: 'merger',         label: 'M&A',           isSpecial: false },
  { id: 'press-releases', label: 'Press Releases',isSpecial: true  },
];

// Extract ticker-like tokens from headlines (2-5 uppercase letters)
function extractTickers(articles) {
  const counts = {};
  const skip = new Set(['A', 'I', 'AT', 'BE', 'BY', 'DO', 'GO', 'IF', 'IN',
    'IS', 'IT', 'NO', 'OF', 'ON', 'OR', 'SO', 'TO', 'UP', 'US', 'WE',
    'AND', 'ARE', 'BUT', 'CEO', 'CFO', 'COO', 'FOR', 'HAS', 'ITS', 'NEW',
    'NOT', 'NOW', 'THE', 'TOP', 'WAS', 'WHO', 'WHY', 'ETF', 'IPO', 'GDP',
    'FED', 'SEC', 'NYSE', 'NASDAQ', 'S&P', 'AI', 'IT', 'Q1', 'Q2', 'Q3', 'Q4']);
  articles.forEach(a => {
    const tokens = (a.headline || '').match(/\b[A-Z]{2,5}\b/g) || [];
    tokens.forEach(t => {
      if (!skip.has(t)) counts[t] = (counts[t] || 0) + 1;
    });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([ticker, count]) => ({ ticker, count }));
}

// ── Watchlist sidebar ────────────────────────────────────────────────────────
function WatchlistSidebar({ isLoggedIn, token, watchlistFilter, setWatchlistFilter,
  onRequestInvite, onSignIn }) {
  const [wlTickers, setWlTickers] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch(API_URL + '/api/watchlist', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(d => {
      if (d.success) setWlTickers(d.tickers || []);
    }).catch(() => {});
  }, [token]);

  if (!isLoggedIn) {
    return (
      <div style={{ padding: '1.25rem', background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
        marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
          letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
          MY WATCHLIST FILTER 🔒
        </div>
        <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6,
          marginBottom: '1rem' }}>
          Filter news to only show stories about stocks in your watchlist.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button onClick={onSignIn}
            style={{ padding: '0.55rem 1rem',
              background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
              border: 'none', borderRadius: '8px', color: '#050d1a',
              fontWeight: 800, cursor: 'pointer', fontSize: '0.8rem',
              fontFamily: 'inherit' }}>
            Sign In
          </button>
          <button onClick={onRequestInvite}
            style={{ padding: '0.55rem 1rem', background: 'transparent',
              border: '1px solid rgba(0,255,136,0.3)', borderRadius: '8px',
              color: '#00ff88', fontWeight: 700, cursor: 'pointer',
              fontSize: '0.8rem', fontFamily: 'inherit' }}>
            Request Invite
          </button>
        </div>
      </div>
    );
  }

  if (wlTickers.length === 0) return null;

  return (
    <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
      marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
        letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
        MY WATCHLIST FILTER
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
        marginBottom: watchlistFilter.length > 0 ? '0.6rem' : 0 }}>
        {wlTickers.map(t => {
          const active = watchlistFilter.includes(t);
          return (
            <button key={t}
              onClick={() => setWatchlistFilter(prev =>
                active ? prev.filter(x => x !== t) : [...prev, t]
              )}
              style={{ padding: '0.3rem 0.65rem',
                background: active ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: active ? '1px solid rgba(0,212,255,0.4)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '20px', color: active ? '#00d4ff' : '#94a3b8',
                fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit' }}>
              {t}
            </button>
          );
        })}
      </div>
      {watchlistFilter.length > 0 && (
        <button onClick={() => setWatchlistFilter([])}
          style={{ fontSize: '0.7rem', color: '#64748b', background: 'none',
            border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
          ✕ Clear filter
        </button>
      )}
    </div>
  );
}

// ── Trending tickers sidebar ─────────────────────────────────────────────────
function TrendingSidebar({ articles, onNavigate }) {
  const trending = extractTickers(articles);
  if (trending.length === 0) return null;
  return (
    <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
        letterSpacing: '0.08em', marginBottom: '0.85rem' }}>
        🔥 TRENDING IN HEADLINES
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {trending.map(({ ticker, count }) => (
          <button key={ticker}
            onClick={() => onNavigate('stock', { ticker })}
            style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '0.4rem 0.6rem',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '6px', cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s' }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)';
              e.currentTarget.style.background = 'rgba(0,212,255,0.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
            }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#00d4ff' }}>
              {ticker}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#475569' }}>
              {count} mention{count !== 1 ? 's' : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Article card ─────────────────────────────────────────────────────────────
function ArticleCard({ article, isLast }) {
  const sentiment = getSentiment(article.headline);
  const ss = SENTIMENT_STYLE[sentiment];
  return (
    <a href={article.url} target="_blank" rel="noopener noreferrer"
      style={{ display: 'block', textDecoration: 'none', padding: '16px 0',
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {article.image && (
          <img src={article.image} alt=""
            onError={e => { e.target.style.display = 'none'; }}
            style={{ width: 80, height: 60, objectFit: 'cover',
              borderRadius: 8, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f8fafc', fontSize: '0.9rem', fontWeight: 600,
            lineHeight: 1.4, marginBottom: 6,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {article.headline}
          </div>
          {article.summary && (
            <div style={{ color: '#8b8ba7', fontSize: '0.78rem', lineHeight: 1.5,
              marginBottom: 8, overflow: 'hidden', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {article.summary}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {article.source}
            </span>
            <span style={{ fontSize: 11, color: '#8b8ba7' }}>
              · {timeAgo(article.datetime)}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '0.15rem 0.5rem',
              borderRadius: 20, background: ss.bg,
              border: `1px solid ${ss.border}`, color: ss.color,
              letterSpacing: '0.05em' }}>
              {sentiment}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6366f1' }}>
              Read →
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Main NewsPage ────────────────────────────────────────────────────────────
export default function NewsPage({ isLoggedIn, token, onRequestInvite, onSignIn, onNavigate }) {
  const [articles,        setArticles]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [category,        setCategory]        = useState('general');
  const [sentimentFilter, setSentimentFilter] = useState('ALL');
  const [watchlistFilter, setWatchlistFilter] = useState([]);
  const [visible,         setVisible]         = useState(20);
  const [companyMode,     setCompanyMode]     = useState(null); // { ticker, name } — NEWS-MULTI-SOURCE

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setVisible(20);
    // NEWS-MULTI-SOURCE: economy/press-releases hit dedicated endpoints —
    // the `category` param below is only meaningful for the original
    // Finnhub-backed general feed, so branch before touching it at all.
    const isSpecial = category === 'economy' || category === 'press-releases';
    const url = isSpecial
      ? `${API_URL}/api/seo/news/${category}`
      : `${API_URL}/api/seo/news?category=${category}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setArticles(d.articles || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(isSpecial ? 'Unavailable' : 'News unavailable'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [category]);

  // Apply filters
  const isSpecialTab = category === 'economy' || category === 'press-releases';
  let filtered = articles;
  // NEWS-MULTI-SOURCE: guard against a stale sentimentFilter value (e.g. set
  // to BULLISH on the general tab, then switched to Fed & Economy) silently
  // filtering content the UI no longer shows a control for — pills being
  // hidden must mean this filter is truly inert, not just invisible.
  if (!isSpecialTab && sentimentFilter !== 'ALL') {
    filtered = filtered.filter(a => getSentiment(a.headline) === sentimentFilter);
  }
  if (watchlistFilter.length > 0) {
    filtered = filtered.filter(a => {
      const text = ((a.headline || '') + ' ' + (a.summary || '')).toUpperCase();
      return watchlistFilter.some(t => text.includes(t));
    });
  }
  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <div>

      {/* ── Header ── */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ margin: '0 0 0.4rem', fontSize: '1.6rem', fontWeight: 900,
          color: '#f8fafc', letterSpacing: '-0.02em' }}>
          Market News
        </h1>
        <div style={{ fontSize: '0.8rem', color: '#475569' }}>
          Latest financial news · Algorithmic sentiment · Not investment advice
        </div>
      </div>

      {/* ── Ticker search — NEWS-MULTI-SOURCE. Selecting a company switches to
          company mode: reuses NewsFeed.jsx (same component StockPage's News
          tab already uses), backed by the same 5-source aggregated endpoint. */}
      <div style={{ marginBottom: '1.5rem', maxWidth: 420 }}>
        <TickerSearchInput
          placeholder="Search news for a specific company…"
          onSelect={(ticker, name) => setCompanyMode({ ticker, name })}
        />
      </div>

      {companyMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#f8fafc' }}>
            {companyMode.name} ({companyMode.ticker})
          </span>
          <button onClick={() => setCompanyMode(null)}
            style={{ padding: '0.35rem 0.85rem', background: 'none', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' }}>
            ← Back to News
          </button>
        </div>
      )}

      {/* ── Company mode — reuses NewsFeed.jsx directly, category/sentiment
          controls hidden since they apply to the general feed, not one company ── */}
      {companyMode && (
        <NewsFeed ticker={companyMode.ticker} />
      )}

      {/* ── General mode — unchanged layout, only rendered when no company selected ── */}
      {!companyMode && (
      <div style={{ display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) 210px',
        gap: '1.25rem', alignItems: 'start' }}>

        {/* ── Main panel ── */}
        <div>
          {/* Single ordered tab bar (TABS array) — Fed & Economy and Press
              Releases are interleaved by display order, not grouped/divided;
              isSpecial per-tab controls color + fetch/sentiment behavior. */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)',
            marginBottom: '1rem', gap: 0, overflowX: 'auto', alignItems: 'center' }}>
            {TABS.map(tab => {
              const isActive = category === tab.id;
              const activeColor = tab.isSpecial ? '#a78bfa' : '#00d4ff';
              return (
                <button key={tab.id} onClick={() => setCategory(tab.id)}
                  style={{ padding: '0.65rem 1rem', background: 'none', border: 'none',
                    borderBottom: isActive ? `2px solid ${activeColor}` : '2px solid transparent',
                    color: isActive ? activeColor : '#64748b',
                    fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                    fontSize: '0.82rem', fontFamily: 'inherit',
                    whiteSpace: 'nowrap', marginBottom: '-1px' }}>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Sentiment filter pills — hidden for economy/press-releases:
              applying BULLISH/BEARISH keyword classification to an official
              Fed statement or a raw company press release would itself be a
              form of misleading editorializing, exactly what precise tab
              naming is meant to avoid. */}
          {category !== 'economy' && category !== 'press-releases' && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem',
            flexWrap: 'wrap' }}>
            {['ALL', 'BULLISH', 'NEUTRAL', 'BEARISH'].map(s => {
              const active = sentimentFilter === s;
              const ss = s === 'ALL' ? null : SENTIMENT_STYLE[s];
              return (
                <button key={s} onClick={() => setSentimentFilter(s)}
                  style={{ padding: '0.3rem 0.85rem',
                    background: active
                      ? (ss ? ss.bg : 'rgba(0,212,255,0.1)')
                      : 'rgba(255,255,255,0.03)',
                    border: active
                      ? `1px solid ${ss ? ss.border : 'rgba(0,212,255,0.3)'}`
                      : '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '20px',
                    color: active ? (ss ? ss.color : '#00d4ff') : '#64748b',
                    fontSize: '0.75rem', fontWeight: active ? 700 : 500,
                    cursor: 'pointer', fontFamily: 'inherit' }}>
                  {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
                </button>
              );
            })}
          </div>
          )}

          {/* Article list */}
          <div style={{ background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: '12px', padding: '0 20px' }}>
            {loading && (
              <div style={{ padding: '3rem 0', textAlign: 'center',
                color: '#64748b', fontSize: '0.88rem' }}>⏳ Loading news…</div>
            )}
            {!loading && error && (
              <div style={{ padding: '3rem 0', textAlign: 'center',
                color: '#64748b', fontSize: '0.88rem' }}>{error}</div>
            )}
            {!loading && !error && shown.length === 0 && (
              <div style={{ padding: '3rem 0', textAlign: 'center',
                color: '#64748b', fontSize: '0.88rem' }}>
                No articles match the current filter.
              </div>
            )}
            {!loading && !error && shown.map((a, i) => (
              <ArticleCard key={a.id || i} article={a} isLast={i === shown.length - 1} />
            ))}
          </div>

          {/* Load more */}
          {!loading && !error && hasMore && (
            <button onClick={() => setVisible(v => v + 20)}
              style={{ width: '100%', marginTop: 12, padding: '0.65rem',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
                color: '#64748b', fontSize: '0.82rem', cursor: 'pointer',
                fontFamily: 'inherit', fontWeight: 600 }}
              onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; }}>
              Load more ({filtered.length - visible} remaining)
            </button>
          )}

          <div style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#475569' }}>
            ⚠️ Sentiment is algorithmic only. Not investment advice.
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div>
          <WatchlistSidebar
            isLoggedIn={isLoggedIn}
            token={token}
            watchlistFilter={watchlistFilter}
            setWatchlistFilter={setWatchlistFilter}
            onRequestInvite={onRequestInvite}
            onSignIn={onSignIn}
          />
          <TrendingSidebar articles={articles} onNavigate={onNavigate} />
        </div>
      </div>
      )}
    </div>
  );
}
